import React, { useMemo, useState } from "react";
import { Button } from "../../../../../design-system";
import mediaOps from "../../../../../database/mediaOps/mediaOpsApi";
import { formatInZone, isoInZone, localPartsInZone, resolveLocalTime } from "../../../../../database/mediaOps/zonedTime";
import { useMutation } from "./hooks";
import { modeLabel } from "./stateLabels";
import { Banner, CheckLabel, FieldLabel, FormGrid, Hint, Mono, Panel, PanelHead, ResultNotice, Row, Small, TextInput } from "./ui";

// Schedules one approved target. The operator picks the intended publish time
// in the account's own time zone. The browser turns it into an ISO instant
// with the correct offset for that date (DST aware). The dispatch lead is
// explicit. "Send now" is a separate, confirmed choice. Never implicit.

export type ScheduleTarget = {
  accountId: string;
  handle: string;
  timezone: string;
  deliveryMode: string;
  approvalId: string;
};

export type ScheduleComputation =
  | { ok: true; intendedIso: string; intendedMs: number; dispatchIso: string; dispatchMs: number; note?: string }
  | { ok: false; message: string };

/** Pure: local date/time in the zone plus a lead in minutes to the two instants. */
export const computeSchedule = (localDate: string, localTime: string, timezone: string, leadMinutes: number): ScheduleComputation => {
  let r;
  try {
    r = resolveLocalTime(localDate, localTime, timezone);
  } catch (e: any) {
    return { ok: false, message: e?.message || "Enter a date and a time." };
  }
  if (r.kind === "nonexistent") {
    return {
      ok: false,
      message: `That time does not exist in ${timezone} because of a clock change. Try ${r.before} or ${r.after}.`,
    };
  }
  if (!Number.isFinite(leadMinutes) || leadMinutes < 0 || leadMinutes > 7 * 24 * 60) {
    return { ok: false, message: "The lead must be between 0 minutes and 7 days." };
  }
  const dispatchMs = r.ms - Math.round(leadMinutes) * 60000;
  return {
    ok: true,
    intendedIso: r.iso,
    intendedMs: r.ms,
    dispatchIso: isoInZone(dispatchMs, timezone),
    dispatchMs,
    note: r.kind === "ambiguous" ? `This time happens twice in ${timezone}. The first one is used (${r.iso}), not ${r.laterIso}.` : undefined,
  };
};

const deliveryPhrase = (mode: string) =>
  mode === "tiktok_inbox" ? "The draft reaches the TikTok inbox at" : mode === "auto_publish" ? "The provider receives it at" : "The owner gets the pack at";

export const ScheduleDialog: React.FC<{
  communityId: string;
  projectId: string;
  revisionId: string;
  target: ScheduleTarget;
  onClose: () => void;
  onScheduled?: () => void;
  now?: () => number;
}> = ({ communityId, projectId, revisionId, target, onClose, onScheduled, now = Date.now }) => {
  const initial = useMemo(() => localPartsInZone(now() + 24 * 60 * 60 * 1000, target.timezone), [now, target.timezone]);
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState("19:00");
  const [lead, setLead] = useState("60");
  const [sendNow, setSendNow] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const m = useMutation<{ scheduleId?: string; targets?: Array<Record<string, unknown>> }>();
  const edit = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setConfirming(false);
    m.edited();
  };

  const direct = target.deliveryMode === "auto_publish";
  const comp = computeSchedule(date, time, target.timezone, direct ? 0 : Number(lead));

  const submit = async () => {
    if (!comp.ok) return;
    if (sendNow && !confirming) {
      setConfirming(true);
      return;
    }
    const res = await m.run((k) =>
      mediaOps.schedule(k, communityId, {
        projectId,
        revisionId,
        targets: [
          {
            accountId: target.accountId,
            deliveryMode: target.deliveryMode,
            approvalId: target.approvalId,
            intendedPublishAt: direct && sendNow ? isoInZone(now(), target.timezone) : comp.intendedIso,
            timezone: target.timezone,
            ...(sendNow ? { dispatchNow: true } : { dispatchAt: comp.dispatchIso }),
          },
        ],
      })
    );
    setConfirming(false);
    if (res && res.status !== "blocked") onScheduled?.();
  };

  const targetsOut = (m.result?.data?.targets || []) as Array<Record<string, any>>;

  return (
    <Panel data-testid="schedule-dialog">
      <PanelHead>
        Schedule @{target.handle} <Small>{modeLabel(target.deliveryMode)}</Small>
      </PanelHead>
      <Hint>Times are in the account time zone: {target.timezone}.</Hint>
      <FormGrid>
        <FieldLabel>
          Publish date
          <TextInput type="date" disabled={direct && sendNow} value={date} onChange={(e) => edit(setDate)(e.target.value)} />
        </FieldLabel>
        <FieldLabel>
          Publish time ({target.timezone})
          <TextInput type="time" disabled={direct && sendNow} value={time} onChange={(e) => edit(setTime)(e.target.value)} />
        </FieldLabel>
        {!direct ? <FieldLabel>
          Dispatch lead in minutes
          <TextInput type="number" min={0} value={lead} disabled={sendNow} onChange={(e) => edit(setLead)(e.target.value)} />
        </FieldLabel> : null}
      </FormGrid>
      {comp.ok ? (
        <Small data-testid="schedule-summary">
          {direct && sendNow ? "Publish as soon as you confirm." : <>Intended publish {formatInZone(comp.intendedMs, target.timezone)}. <Mono>{comp.intendedIso}</Mono></>}
          <br />
          {sendNow ? "Sent as soon as you confirm." : `${deliveryPhrase(target.deliveryMode)} ${formatInZone(comp.dispatchMs, target.timezone)}.`}{" "}
          {!sendNow ? <Mono>{comp.dispatchIso}</Mono> : null}
        </Small>
      ) : (
        <Banner $tone="danger">{(comp as { message: string }).message}</Banner>
      )}
      {comp.ok && comp.note ? <Banner $tone="warning">{comp.note}</Banner> : null}
      <CheckLabel>
        <input type="checkbox" checked={sendNow} onChange={(e) => edit(setSendNow)(e.target.checked)} />
        Send now instead of at the dispatch time
      </CheckLabel>
      {confirming ? (
        <Banner $tone="warning">{direct ? "This publishes the approved post right away." : "This sends the pack right away."} It still passes the same review and policy checks. Confirm to send now.</Banner>
      ) : null}
      <Row>
        <Button size="sm" onClick={submit} disabled={!comp.ok} isLoading={m.busy}>
          {sendNow ? (confirming ? "Confirm send now" : "Send now") : "Schedule"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Close
        </Button>
      </Row>
      <ResultNotice result={m.result} okText="Scheduled." />
      {targetsOut.map((t, i) => (
        <Small key={i}>
          @{target.handle}: {String(t.result || t.decision || t.status || t.scheduleState || "")}
          {t.reason ? ` (${t.reason})` : ""}
          {t.targetId ? ` Target ${t.targetId}.` : ""}
        </Small>
      ))}
    </Panel>
  );
};

export default ScheduleDialog;
